import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { RECONCILABLE_ACCOUNTS } from '../accounts';
import {
  computeFileChecksum,
  confirmUpload,
  driveWebViewLink,
  getUploadUrl,
  uploadToSignedUrl,
  getDocuments,
  searchDocuments,
} from '@/features/documents/api';
import { useDebounce } from '@/hooks/useDebounce';
import { useDocumentDetail, useDocumentPreviewUrl, useTags } from '@/features/documents/hooks/useDocuments';
import { getInvoiceByNumber } from '@/features/invoices/api';
// useDebounce is still used for the DMS search input below.
import { useQueryClient } from '@tanstack/react-query';
import {
  createDraftStatement,
  patchStatement,
  putStatementLines,
  startStatement,
} from '../api';
import { Trash2, Plus, FileText, TextIcon, Info, Database, Tag as TagIcon, Braces, ArrowLeft, ExternalLink, X } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { statementsKeys, useStatement } from '../hooks';

/**
 * Full-page reconciliation manager.
 *
 * Route shape:
 *   /acc/statements/new   → blank draft, no id yet
 *   /acc/statements/:id   → edit existing draft
 *
 * The list page navigates here; this page never renders inline.
 */

type Mode = 'upload' | 'pick';

/**
 * Pull an invoice number out of a free-text bank-line description.
 * Only explicit "facture NNNN" / "facture NNNN-1" references count — a bare
 * 4-5 digit token is almost always an account/transfer number (e.g. "UNIV
 * MONTREAL 49711", "dépôt-2974") and produced noisy "no PG match" rows.
 */
function extractInvoiceNumber(desc: string | null | undefined): string | null {
  if (!desc) return null;
  // "facture 4974", "facture: 4974-1", but also "facture UNIVERSIT LAVA 4974-1"
  // (RBC puts the payer name between the word and the number).
  const explicit = desc.match(/\bfactures?\b[^\d]*(\d{4,5})(?:-\d)?\b/i);
  return explicit ? explicit[1] : null;
}

/**
 * Build a QuickBooks Online deep-link for a transaction.
 * Maps the QB txn type to the QBO entity route segment; unknown types fall
 * back to the generic `app` route so the link still lands inside QBO.
 */
function qbDeepLink(type: string | null | undefined, id: string): string {
  const routes: Record<string, string> = {
    Payment: 'recvpayment',
    Deposit: 'deposit',
    Expense: 'expense',
    Purchase: 'expense',
    BillPayment: 'billpayment',
    'Bill Payment': 'billpayment',
    Bill: 'bill',
    Transfer: 'transfer',
    Check: 'check',
    Cheque: 'check',
    JournalEntry: 'journal',
    'Journal Entry': 'journal',
    SalesReceipt: 'salesreceipt',
    Invoice: 'invoice',
    CreditCardPayment: 'expense',
    'Credit Card Payment': 'expense',
  };
  const route = (type && routes[type]) || 'app';
  return `https://app.qbo.intuit.com/app/${route}?txnId=${id}`;
}

/** Inline match info row: shows QB audit details + PG invoice lookup. */
function LineMatchInfo({
  qbStatus,
  qbTxnId,
  qbTxnType,
  qbAmount,
  description,
}: {
  qbStatus: string | null | undefined;
  qbTxnId: string | null | undefined;
  qbTxnType: string | null | undefined;
  qbAmount: string | null | undefined;
  description: string | null | undefined;
}) {
  const invNum = extractInvoiceNumber(description);
  const invoiceQuery = useQuery({
    queryKey: ['statement-line-invoice', invNum],
    queryFn: () => (invNum ? getInvoiceByNumber(invNum) : Promise.resolve(null)),
    enabled: !!invNum,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const inv = invoiceQuery.data;
  const qbAmtNum = qbAmount != null ? Number(qbAmount) : null;
  // Only show the QB segment when it carries real content beyond the label —
  // a bare "QB" prefix under every missing/ambiguous line was pure clutter.
  const showQb =
    qbStatus &&
    qbStatus !== 'not_audited' &&
    (Boolean(qbTxnId) || (qbAmtNum !== null && Number.isFinite(qbAmtNum)));
  const showPg = !!invNum;
  if (!showQb && !showPg) return null;
  const fmt = (n: number) => n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-muted-foreground">
      {showQb && (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-foreground/70">QB</span>
          {qbTxnType && <span>{qbTxnType}</span>}
          {qbTxnId && (
            <a
              href={`https://qbo.intuit.com/app/${(qbTxnType ?? '').toLowerCase()}?txnId=${qbTxnId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-blue-700 hover:underline"
              title="Open in QBO"
            >
              #{qbTxnId}
            </a>
          )}
          {qbAmtNum !== null && Number.isFinite(qbAmtNum) && <span className="font-mono">{fmt(qbAmtNum)}</span>}
        </span>
      )}
      {showPg && (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-foreground/70">PG</span>
          {invoiceQuery.isLoading ? (
            <span className="italic">looking up #{invNum}…</span>
          ) : inv ? (
            <>
              <a
                href={`/erp/invoices?invoice=${inv.id}`}
                className="font-mono text-blue-700 hover:underline"
                title={`Open invoice ${inv.invoiceNumber}`}
              >
                #{inv.invoiceNumber}
              </a>
              {inv.accountName && <span className="truncate max-w-[260px]">— {inv.accountName}</span>}
              <span className="font-mono">{fmt(Number(inv.total))}</span>
              {inv.status && <span className="rounded bg-muted px-1">{inv.status}</span>}
            </>
          ) : (
            <span className="italic text-amber-700">no PG match for #{invNum}</span>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Amount cell for the lines table. Shows a grouped, 2-decimal currency value
 * (e.g. "1,444.37", "1.00") when not being edited, and the raw editable number
 * while focused — so reconciliation review reads like a bank statement, while
 * data entry stays simple. `value` is the absolute amount as a string ('' = empty).
 */
function AmountInput({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (raw: string) => void;
  className?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  // While editing, show the user's exact keystrokes (so "1444." can be typed);
  // when idle, show a grouped 2-decimal currency value.
  const display = focused
    ? draft
    : value === '' || !Number.isFinite(Number(value))
      ? ''
      : Number(value).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <Input
      type="text"
      inputMode="decimal"
      placeholder="0.00"
      value={display}
      onFocus={() => {
        setDraft(value);
        setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        setDraft(raw);
        onChange(raw);
      }}
      className={className}
    />
  );
}

/**
 * Create a bank reconciliation run.
 * Layout mimics a bank statement: account header → period → balances → lines preview.
 * The source document (upload or DMS pick) lives below the statement fields.
 */
export default function StatementDetailPage() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id?: string }>();
  // /acc/statements/new uses sentinel 'new' — treat as create mode.
  const statementId = routeId && routeId !== 'new' ? routeId : null;
  // Confirm-before-delete dialog state for the per-line trash button.
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);

  const closeBack = () => navigate('/acc/statements');
  // The page is always mounted (no panel/modal "open" state); kept as `true`
  // to preserve the existing effect/query gating without rewriting every
  // dependency array.
  const open = true;
  // Statement header fields
  const [qbAccountId, setQbAccountId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [closingBalance, setClosingBalance] = useState('');

  // Source document (the bank statement linked to this recon).
  const [mode, setMode] = useState<Mode>('upload');
  const [dmsDocumentId, setDmsDocumentId] = useState<string | null>(null);
  const [dmsDocumentName, setDmsDocumentName] = useState<string | null>(null);
  // Right-panel preview: defaults to the statement; switches to a per-line
  // proof when the user clicks the DMS button on a row.
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [previewSource, setPreviewSource] = useState<'statement' | 'line'>('statement');
  // The source/preview panel opens only on demand (View statement / line proof).
  const [panelOpen, setPanelOpen] = useState(false);

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Pick state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const queryClient = useQueryClient();
  const [draftId, setDraftId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Editable statement lines (client-side until Save draft).
  // When hydrated from an existing reconciliation, we keep the QB audit fields
  // (qb_status, qb_amount, discrepancy_notes) read-only alongside each line so
  // the user can see at a glance whether the audit found a match.
  interface DraftLine {
    _key: string;
    posted_date: string;
    description: string;
    amount: string;
    reference: string;
    qb_status?: string | null;
    qb_amount?: string | null;
    qb_txn_id?: string | null;
    qb_txn_type?: string | null;
    discrepancy_notes?: string | null;
    dms_document_id?: string | null;
  }
  const makeEmptyLine = (): DraftLine => ({
    _key: Math.random().toString(36).slice(2),
    posted_date: '',
    description: '',
    amount: '',
    reference: '',
  });
  const [lines, setLines] = useState<DraftLine[]>([]);

  // Review-mode line filter: jump straight to the problem lines.
  const [lineFilter, setLineFilter] = useState<string>('all');
  const matchesLineFilter = (l: DraftLine) => {
    if (lineFilter === 'all') return true;
    if (lineFilter === 'unaudited') return !l.qb_status || l.qb_status === 'not_audited';
    return l.qb_status === lineFilter;
  };

  // Per-status counts across all lines (drives the header strip + filter chips).
  const auditCounts = useMemo(() => {
    const c = { found: 0, missing: 0, found_wrong: 0, duplicate: 0, ambiguous: 0, unaudited: 0 };
    for (const l of lines) {
      switch (l.qb_status) {
        case 'found': c.found += 1; break;
        case 'missing': c.missing += 1; break;
        case 'found_wrong': c.found_wrong += 1; break;
        case 'duplicate': c.duplicate += 1; break;
        case 'ambiguous': c.ambiguous += 1; break;
        default: c.unaudited += 1;
      }
    }
    return c;
  }, [lines]);
  const isAudited = lines.length > 0 && auditCounts.unaudited < lines.length;

  const selectedAccount = useMemo(
    () => RECONCILABLE_ACCOUNTS.find((a) => a.id === qbAccountId) ?? null,
    [qbAccountId],
  );

  // Preview: fetch document detail + signed preview URL for the selected DMS doc.
  // Right-panel showing: use previewDocId, falling back to the recon's statement doc.
  const effectivePreviewId = previewDocId ?? dmsDocumentId;
  const docDetailQuery = useDocumentDetail(effectivePreviewId);
  const previewQuery = useDocumentPreviewUrl(effectivePreviewId, !!effectivePreviewId);
  const tagsQuery = useTags();
  const docDetail = docDetailQuery.data;
  const previewUrl = previewQuery.data?.previewUrl ?? null;

  // DocumentDetail type is incomplete — the API returns metadata + tagIds too.
  const docAny = docDetail as unknown as {
    metadata?: Record<string, unknown> | null;
    tagIds?: string[] | null;
  } | undefined;
  const docMetadata = docAny?.metadata ?? null;
  const docTagIds = docAny?.tagIds ?? [];
  // Google Drive copy of the same document, recorded by the Records Manager
  // filer when it was filed (drive_file_id / drive_web_view_link metadata).
  const driveUrl = driveWebViewLink({
    metadata: docMetadata,
    currentVersion: docDetail?.currentVersion,
  });

  const [savingDraft, setSavingDraft] = useState(false);

  // Resizable split: left column width in pixels.
  const [leftWidth, setLeftWidth] = useState(460);
  const splitContainerRef = useRef<HTMLFormElement>(null);
  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftWidth;
    const onMove = (ev: MouseEvent) => {
      const containerWidth = splitContainerRef.current?.clientWidth ?? 0;
      const next = startWidth + (ev.clientX - startX);
      // Clamp: min 360, max containerWidth - 420 so the right pane keeps room.
      const clamped = Math.max(360, Math.min(next, Math.max(420, containerWidth - 420)));
      setLeftWidth(clamped);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Close the on-demand preview panel with the Escape key.
  useEffect(() => {
    if (!panelOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [panelOpen]);

  // Load existing statement when editing.
  const existingQuery = useStatement(open && statementId ? statementId : null);
  const existingStatus = existingQuery.data?.status ?? null;
  // A non-draft statement has already been audited at least once — the primary
  // action re-runs the QB audit rather than "starting" a reconciliation.
  const isReRun = !!statementId && !!existingStatus && existingStatus !== 'draft';

  // Reset state on open. No DB row is created until the user clicks Save draft
  // (unless we're hydrating an existing one).
  useEffect(() => {
    if (!open) {
      setDraftId(null);
      return;
    }
    if (statementId) {
      // Edit mode — wait for the query to load, then hydrate.
      setDraftId(statementId);
    } else {
      // Create mode — clear everything.
      setQbAccountId('');
      setPeriodStart('');
      setPeriodEnd('');
      setOpeningBalance('');
      setClosingBalance('');
      setMode('upload');
      setDmsDocumentId(null);
      setDmsDocumentName(null);
      setFile(null);
      setSearch('');
      setDraftId(null);
      setLines([]);
      setPreviewDocId(null);
      setPreviewSource('statement');
    }
  }, [open, statementId]);

  // Whenever the statement doc changes, default the preview to it.
  useEffect(() => {
    setPreviewDocId(null);
    setPreviewSource('statement');
  }, [dmsDocumentId]);

  // Hydrate from the loaded existing statement.
  useEffect(() => {
    if (!open || !statementId || !existingQuery.data) return;
    const s = existingQuery.data;
    setQbAccountId(s.qb_account_id ?? '');
    setPeriodStart(s.period_start ?? '');
    setPeriodEnd(s.period_end ?? '');
    setOpeningBalance(s.opening_balance ?? '');
    setClosingBalance(s.closing_balance ?? '');
    setDmsDocumentId(s.dms_document_id ?? null);
    setDmsDocumentName(null); // name comes from the document detail query
    setMode('pick');
    setLines(
      (s.lines ?? []).map((l) => ({
        _key: Math.random().toString(36).slice(2),
        posted_date: l.posted_date ?? '',
        description: l.description ?? '',
        amount: l.amount ?? '',
        reference: l.reference ?? '',
        qb_status: l.qb_status ?? null,
        qb_amount: l.qb_amount ?? null,
        qb_txn_id: l.qb_txn_id ?? null,
        qb_txn_type: (l as { qb_txn_type?: string | null }).qb_txn_type ?? null,
        discrepancy_notes: l.discrepancy_notes ?? null,
        dms_document_id: l.dms_document_id ?? null,
      })),
    );
  }, [open, statementId, existingQuery.data]);

  function currentFields() {
    return {
      qb_account_id: qbAccountId || null,
      period_start: periodStart || null,
      period_end: periodEnd || null,
      opening_balance: openingBalance || null,
      closing_balance: closingBalance || null,
      dms_document_id: dmsDocumentId,
    };
  }

  function linesForApi() {
    return lines
      .filter((l) => l.posted_date && l.amount !== '')
      .map((l) => ({
        posted_date: l.posted_date,
        description: l.description || null,
        amount: l.amount,
        reference: l.reference || null,
      }));
  }

  async function handleSaveDraft(): Promise<string | null> {
    setSavingDraft(true);
    try {
      let id = draftId;
      if (id) {
        await patchStatement(id, currentFields());
      } else {
        const created = await createDraftStatement(currentFields());
        id = created.id;
        setDraftId(id);
      }
      await putStatementLines(id!, linesForApi());
      toast.success(draftId ? 'Draft saved' : 'Draft created');
      queryClient.invalidateQueries({ queryKey: statementsKeys.all });
      return id;
    } catch (err) {
      toast.error('Could not save draft', { description: (err as Error).message });
      return null;
    } finally {
      setSavingDraft(false);
    }
  }

  // Auto-populate period from a filename / document name.
  function autoPopulateFromName(name: string) {
    const monthMatch = name.match(/\b(19|20)(\d{2})[-_ ](0?[1-9]|1[0-2])\b/);
    if (monthMatch) {
      const year = `${monthMatch[1]}${monthMatch[2]}`;
      const month = monthMatch[3].padStart(2, '0');
      const start = `${year}-${month}-01`;
      const endDate = new Date(Number(year), Number(month), 0);
      const end = `${year}-${month}-${String(endDate.getDate()).padStart(2, '0')}`;
      setPeriodStart(start);
      setPeriodEnd(end);
    }
  }

  // ------ Upload flow ------
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    setDmsDocumentId(null);
    setDmsDocumentName(picked?.name ?? null);
    if (picked) autoPopulateFromName(picked.name);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    try {
      const { documentId, uploadUrl } = await getUploadUrl(
        file.name,
        file.type || 'application/pdf',
        { uploadSource: 'bank-reconciliation' },
      );
      await uploadToSignedUrl(uploadUrl, file);
      const checksum = await computeFileChecksum(file);
      await confirmUpload(documentId, file.size, checksum);
      setDmsDocumentId(documentId);
      toast.success('Statement uploaded to DMS');
    } catch (err) {
      toast.error('Upload failed', { description: (err as Error).message });
    } finally {
      setUploading(false);
    }
  }

  // ------ Pick-from-DMS flow ------
  const docsQuery = useQuery({
    queryKey: ['dms-docs-for-statement', debouncedSearch],
    queryFn: () => (debouncedSearch ? searchDocuments(debouncedSearch) : getDocuments(null)),
    enabled: open && mode === 'pick',
  });

  const docs = useMemo(() => docsQuery.data?.data ?? [], [docsQuery.data]);

  function handlePick(docId: string, docName: string) {
    setDmsDocumentId(docId);
    setDmsDocumentName(docName);
    autoPopulateFromName(docName);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!qbAccountId) return toast.error('Please select an account');
    if (!periodStart || !periodEnd) return toast.error('Period start and end are required');
    if (!dmsDocumentId) return toast.error('Please upload or pick a statement document');

    setStarting(true);
    try {
      // Ensure the row exists and has the latest field values before starting.
      const id = await handleSaveDraft();
      if (!id) return;
      await startStatement(id);
      toast.success('Reconciliation started');
      queryClient.invalidateQueries({ queryKey: statementsKeys.all });
      closeBack();
    } catch (err) {
      toast.error('Failed to start reconciliation', { description: (err as Error).message });
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] min-h-0 -mx-6 -mt-6">
      <div className="flex items-center justify-between border-b bg-background px-6 py-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={closeBack}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Statements
          </Button>
          <div className="h-5 w-px bg-border" />
          <h1 className="text-base font-semibold truncate">
            {statementId ? 'Reconciliation' : 'New reconciliation'}
          </h1>
          {existingStatus && (
            <Badge variant={existingStatus === 'completed' ? 'default' : 'secondary'}>
              {existingStatus === 'in_progress' ? 'In progress' : existingStatus.charAt(0).toUpperCase() + existingStatus.slice(1)}
            </Badge>
          )}
          {isAudited && (
            <div className="hidden md:flex items-center gap-1 text-[11px]">
              <span className="rounded border border-green-200 bg-green-50 px-1.5 font-medium text-green-700" title={`${auditCounts.found} matched in QB`}>
                ✓ {auditCounts.found}
              </span>
              {auditCounts.missing > 0 && (
                <span className="rounded border border-red-200 bg-red-50 px-1.5 font-medium text-red-700" title={`${auditCounts.missing} missing in QB`}>
                  – {auditCounts.missing}
                </span>
              )}
              {auditCounts.found_wrong > 0 && (
                <span className="rounded border border-amber-200 bg-amber-50 px-1.5 font-medium text-amber-700" title={`${auditCounts.found_wrong} wrong amount`}>
                  ≠ {auditCounts.found_wrong}
                </span>
              )}
              {auditCounts.duplicate > 0 && (
                <span className="rounded border border-purple-200 bg-purple-50 px-1.5 font-medium text-purple-700" title={`${auditCounts.duplicate} duplicate matches`}>
                  ⧉ {auditCounts.duplicate}
                </span>
              )}
              {(auditCounts.ambiguous > 0 || auditCounts.unaudited > 0) && (
                <span className="rounded border border-slate-200 bg-slate-50 px-1.5 font-medium text-slate-600" title={`${auditCounts.ambiguous} ambiguous · ${auditCounts.unaudited} not audited`}>
                  ? {auditCounts.ambiguous + auditCounts.unaudited}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={closeBack}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleSaveDraft}
            disabled={savingDraft || starting}
          >
            {savingDraft ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            type="submit"
            form="statement-form"
            size="sm"
            disabled={!dmsDocumentId || starting || savingDraft}
          >
            {starting
              ? (isReRun ? 'Re-running…' : 'Starting…')
              : (isReRun ? 'Re-run QB audit' : 'Start reconciliation')}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <form
          id="statement-form"
          onSubmit={handleSubmit}
          ref={splitContainerRef}
          className="flex min-w-0 flex-1 overflow-hidden p-4"
        >
          {/* ================= LEFT COLUMN — Form ================= */}
          <div
            className="space-y-5 min-w-0 overflow-y-auto pr-3"
            style={panelOpen ? { width: leftWidth, flexShrink: 0 } : { width: '100%' }}
          >
          {/* ===================================================================
              Step 1 — Account (standalone)
              =================================================================== */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="qbAccountId" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                1. Account
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => {
                  setPreviewDocId(null);
                  setPreviewSource('statement');
                  setPanelOpen(true);
                }}
              >
                <FileText className="h-3.5 w-3.5" />
                View statement
              </Button>
            </div>
            <p className="sr-only">Pick the account. Then pick a source document in the right panel.</p>
            <Select value={qbAccountId} onValueChange={setQbAccountId}>
              <SelectTrigger id="qbAccountId" className="text-base font-medium">
                <SelectValue placeholder="Select an account…" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Bank accounts</SelectLabel>
                  {RECONCILABLE_ACCOUNTS.filter((a) => a.kind === 'bank').map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Credit cards</SelectLabel>
                  {RECONCILABLE_ACCOUNTS.filter((a) => a.kind === 'credit_card').map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {selectedAccount && (
              <p className="text-xs text-muted-foreground">
                {selectedAccount.kind === 'bank' ? 'Bank' : 'Credit card'} · {selectedAccount.subtype} · QB Id {selectedAccount.id}
              </p>
            )}
          </div>

          {/* ===================================================================
              Step 2 — Statement header (period, balances, lines)
              =================================================================== */}
          <div className="border rounded-lg p-5 bg-muted/30 space-y-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              2. Statement details
            </div>

            {/* Period row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="periodStart" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Statement period — from
                </Label>
                <Input
                  id="periodStart"
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="periodEnd" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  To
                </Label>
                <Input
                  id="periodEnd"
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Balances row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="openingBalance" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Opening balance
                </Label>
                <Input
                  id="openingBalance"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="closingBalance" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Closing balance
                </Label>
                <Input
                  id="closingBalance"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={closingBalance}
                  onChange={(e) => setClosingBalance(e.target.value)}
                />
              </div>
            </div>

            {/* Editable statement lines */}
            <div className="border-t pt-3 space-y-2">
              {isAudited && (
                <div className="flex flex-wrap items-center gap-1 text-[11px]">
                  {([
                    { key: 'all', label: 'All', count: lines.length },
                    { key: 'found', label: '✓ Found', count: auditCounts.found },
                    { key: 'missing', label: '– Missing', count: auditCounts.missing },
                    { key: 'found_wrong', label: '≠ Wrong amount', count: auditCounts.found_wrong },
                    { key: 'duplicate', label: '⧉ Duplicate', count: auditCounts.duplicate },
                    { key: 'ambiguous', label: '? Ambiguous', count: auditCounts.ambiguous },
                    { key: 'unaudited', label: '○ Unaudited', count: auditCounts.unaudited },
                  ]).filter((c) => c.key === 'all' || c.count > 0).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setLineFilter(c.key)}
                      aria-pressed={lineFilter === c.key}
                      className={`rounded border px-2 py-0.5 font-medium transition-colors ${
                        lineFilter === c.key
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {c.label} <span className="opacity-70">{c.count}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px]">Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[120px] text-right">Debit</TableHead>
                      <TableHead className="w-[120px] text-right">Credit</TableHead>
                      <TableHead className="w-[120px] text-right">Balance</TableHead>
                      <TableHead className="w-[52px] text-center" title="QB audit match status">QB</TableHead>
                      <TableHead className="w-[52px] text-center" title="DMS source document">DMS</TableHead>
                      <TableHead className="w-[44px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground italic py-6">
                          No lines yet. Click “Add line” to start entering them.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (() => {
                        const ob = Number(openingBalance) || 0;
                        let running = ob;
                        // Running balance is computed over ALL lines — filtering
                        // only hides rows, it never changes the balances.
                        const withRunning = lines.map((line, idx) => {
                          const n = Number(line.amount) || 0;
                          running += n;
                          return { idx, line, running };
                        });
                        const visible = withRunning.filter(({ line }) => matchesLineFilter(line));
                        if (visible.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center text-muted-foreground italic py-6">
                                No lines match this filter.
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return visible.map(({ idx, line, running }) => {
                        const qbBadge = (() => {
                          const s = line.qb_status;
                          if (!s || s === 'not_audited') return null;
                          const map: Record<string, { label: string; cls: string; title?: string }> = {
                            found:       { label: '✓',  cls: 'bg-green-100 text-green-800 border-green-200',     title: line.qb_txn_id ? `Matched ${line.qb_txn_type ?? 'QB'} #${line.qb_txn_id}` : 'Matched QB transaction' },
                            missing:     { label: '–',  cls: 'bg-red-100 text-red-800 border-red-200',           title: 'No QB record' },
                            found_wrong: { label: '≠',  cls: 'bg-amber-100 text-amber-800 border-amber-200',     title: line.discrepancy_notes || 'Amount mismatch' },
                            duplicate:   { label: '⧉',  cls: 'bg-orange-100 text-orange-800 border-orange-200', title: line.discrepancy_notes || 'Duplicate match' },
                            ambiguous:   { label: '?',  cls: 'bg-slate-100 text-slate-800 border-slate-200',     title: line.discrepancy_notes || 'Multiple near matches' },
                          };
                          return map[s] ?? { label: '?', cls: 'bg-slate-100 text-slate-800 border-slate-200', title: s };
                        })();
                        const n = Number(line.amount);
                        const isNeg = Number.isFinite(n) && n < 0;
                        const isPos = Number.isFinite(n) && n > 0;
                        const debitStr = isNeg ? Math.abs(n).toString() : '';
                        const creditStr = isPos ? n.toString() : '';
                        // Mirror LineMatchInfo's own gating so we never render an
                        // empty sub-row (a bare "QB" label under every line).
                        const hasMatchInfo =
                          Boolean(line.qb_txn_id) ||
                          (line.qb_amount != null && Number.isFinite(Number(line.qb_amount))) ||
                          !!extractInvoiceNumber(line.description);
                        return (
                          <Fragment key={line._key}>
                            <TableRow className="hover:bg-muted/30">
                              <TableCell className="p-1">
                                <Input
                                  type="date"
                                  value={line.posted_date}
                                  onChange={(e) =>
                                    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, posted_date: e.target.value } : l)))
                                  }
                                  className="h-8 text-xs"
                                />
                              </TableCell>
                              <TableCell className="p-1">
                                <Input
                                  placeholder="Description"
                                  value={line.description}
                                  onChange={(e) =>
                                    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, description: e.target.value } : l)))
                                  }
                                  className="h-8 text-xs"
                                />
                              </TableCell>
                              <TableCell className="p-1">
                                <AmountInput
                                  value={debitStr}
                                  onChange={(v) => {
                                    const next = v === '' ? '' : `-${Math.abs(Number(v) || 0)}`;
                                    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, amount: next } : l)));
                                  }}
                                  className="h-8 text-xs text-right font-mono tabular-nums text-red-700"
                                />
                              </TableCell>
                              <TableCell className="p-1">
                                <AmountInput
                                  value={creditStr}
                                  onChange={(v) => {
                                    const next = v === '' ? '' : Math.abs(Number(v) || 0).toString();
                                    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, amount: next } : l)));
                                  }}
                                  className="h-8 text-xs text-right font-mono tabular-nums text-green-700"
                                />
                              </TableCell>
                              <TableCell className="text-right text-xs font-mono tabular-nums text-muted-foreground">
                                {running.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })}
                              </TableCell>
                              <TableCell className="text-center" title={qbBadge?.title ?? 'Not yet audited'}>
                                <div className="inline-flex items-center justify-center gap-1">
                                  {qbBadge ? (
                                    <span className={`inline-flex h-6 w-7 items-center justify-center rounded border text-[11px] font-medium ${qbBadge.cls}`}>
                                      {qbBadge.label}
                                    </span>
                                  ) : (
                                    <span className="text-[11px] text-muted-foreground">—</span>
                                  )}
                                  {line.qb_txn_id && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <a
                                            href={qbDeepLink(line.qb_txn_type, line.qb_txn_id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-muted-foreground hover:text-blue-700"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                          </a>
                                        </TooltipTrigger>
                                        <TooltipContent>Open transaction in QuickBooks</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {line.dms_document_id && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPreviewDocId(line.dms_document_id ?? null);
                                      setPreviewSource('line');
                                      setPanelOpen(true);
                                    }}
                                    className={`inline-flex h-6 w-7 items-center justify-center rounded border text-[11px] font-medium ${
                                      previewDocId === line.dms_document_id && previewSource === 'line'
                                        ? 'bg-sky-200 text-sky-900 border-sky-400 ring-1 ring-sky-400'
                                        : 'bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100'
                                    }`}
                                    title={`Open DMS proof ${line.dms_document_id.slice(0, 8)}…`}
                                  >
                                    <FileText className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteIdx(idx)}
                                  className="text-muted-foreground hover:text-destructive"
                                  title="Remove line"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </TableCell>
                            </TableRow>
                            {hasMatchInfo && (
                              <TableRow className="hover:bg-transparent border-t-0">
                                <TableCell colSpan={8} className="pt-0 pb-1.5 pl-3">
                                  <LineMatchInfo
                                    qbStatus={line.qb_status}
                                    qbTxnId={line.qb_txn_id}
                                    qbTxnType={line.qb_txn_type}
                                    qbAmount={line.qb_amount}
                                    description={line.description}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                        });
                      })()
                    )}
                  </TableBody>
                </Table>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs"
                onClick={() => setLines((prev) => [...prev, makeEmptyLine()])}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add line
              </Button>

              {lines.length > 0 && (() => {
                const pos = lines.reduce((s, l) => { const n = Number(l.amount) || 0; return n > 0 ? s + n : s; }, 0);
                const neg = lines.reduce((s, l) => { const n = Number(l.amount) || 0; return n < 0 ? s + n : s; }, 0);
                const ob = Number(openingBalance);
                const fmt = (n: number) => n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
                // Pull statement-declared totals from the linked DMS doc
                // metadata (extracted at classification time).
                const stmtMeta = (docDetail as unknown as { metadata?: Record<string, unknown> | null })?.metadata ?? null;
                const stmtOpening = stmtMeta?.opening_balance != null ? Number(stmtMeta.opening_balance) : null;
                const stmtClosing = stmtMeta?.closing_balance != null ? Number(stmtMeta.closing_balance) : null;
                const stmtCredits = stmtMeta?.total_credits != null ? Number(stmtMeta.total_credits) : null;
                const stmtDebits  = stmtMeta?.total_debits  != null ? Number(stmtMeta.total_debits)  : null;
                // Exact-cent compare: round each side to cents, require equality.
                const cents = (n: number) => Math.round(n * 100);
                const eq = (a: number | null, b: number | null) =>
                  a !== null && b !== null && Number.isFinite(a) && Number.isFinite(b) && cents(a) === cents(b);
                const checks: Array<{ label: string; entered: number; expected: number | null; ok: boolean; tone: 'credit' | 'debit' | 'neutral' }> = [
                  { label: 'Opening balance', entered: ob,        expected: stmtOpening, ok: eq(ob, stmtOpening),                 tone: 'neutral' },
                  { label: 'Σ Credits',        entered: pos,       expected: stmtCredits, ok: eq(pos, stmtCredits),                 tone: 'credit' },
                  { label: 'Σ Debits',         entered: Math.abs(neg), expected: stmtDebits, ok: eq(Math.abs(neg), stmtDebits),    tone: 'debit' },
                  { label: 'Closing balance',  entered: ob + neg + pos, expected: stmtClosing, ok: eq(ob + neg + pos, stmtClosing), tone: 'neutral' },
                ];
                const allOk = checks.every((c) => c.ok);
                const anyExpected = checks.some((c) => c.expected !== null);
                return (
                  <div className="space-y-2 pt-2 border-t">
                    {anyExpected ? (
                      <div className={`rounded-md border text-xs ${allOk ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                        <div className={`px-3 py-1.5 font-medium ${allOk ? 'text-green-900' : 'text-amber-900'}`}>
                          {allOk ? '✓ All 4 checks pass' : `⚠ ${checks.filter((c) => !c.ok && c.expected !== null).length} of ${checks.filter((c) => c.expected !== null).length} checks fail`}
                        </div>
                        <table className="w-full text-[11px] font-mono">
                          <tbody>
                            {checks.map((c) => {
                              const toneClass = c.tone === 'credit' ? 'text-green-700' : c.tone === 'debit' ? 'text-red-700' : '';
                              const delta = c.expected !== null ? cents(c.entered) - cents(c.expected) : 0;
                              return (
                                <tr key={c.label} className="border-t border-current/10">
                                  <td className="px-3 py-1 w-32 text-muted-foreground">{c.label}</td>
                                  <td className={`px-2 py-1 text-right ${toneClass}`}>{fmt(c.entered)}</td>
                                  <td className="px-1 py-1 text-center text-muted-foreground">vs</td>
                                  <td className="px-2 py-1 text-right text-muted-foreground">
                                    {c.expected !== null ? fmt(c.expected) : '—'}
                                  </td>
                                  <td className="px-3 py-1 w-16 text-right">
                                    {c.expected === null ? (
                                      <span className="text-muted-foreground">no source</span>
                                    ) : c.ok ? (
                                      <span className="text-green-700">✓</span>
                                    ) : (
                                      <span className="text-amber-800">Δ{fmt(delta / 100)}</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="rounded-md border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
                        Link a statement document to enable the 4-check validation (opening, credits, debits, closing).
                      </div>
                    )}
                    <div className="text-[11px] text-muted-foreground">
                      {lines.filter((l) => l.posted_date && l.amount !== '').length} of {lines.length} lines complete
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          </div>
          {/* ================= END LEFT COLUMN ================= */}

          {panelOpen && (
          <>
          {/* ================= DRAG HANDLE ================= */}
          <div
            onMouseDown={startDrag}
            className="hidden lg:flex items-center justify-center w-2 mx-2 cursor-col-resize bg-border hover:bg-primary/60 active:bg-primary transition-colors rounded shrink-0 relative group"
            title="Drag to resize"
          >
            <div className="absolute inset-y-0 -left-2 -right-2" />
            <div className="w-0.5 h-8 bg-background/60 group-hover:bg-background rounded" />
          </div>

          {/* ================= RIGHT COLUMN — Source picker / Preview ================= */}
          <div className="hidden lg:flex flex-col min-w-0 min-h-0 border rounded-lg bg-muted/20 overflow-hidden flex-1">
            {!dmsDocumentId ? (
              // Source document picker — takes the whole right pane until a doc is linked.
              <div className="flex-1 flex flex-col min-h-0 p-4 gap-3 overflow-y-auto">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Source document
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Upload a new statement PDF or pick an existing one from DMS.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPanelOpen(false)}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    title="Close panel (Esc)"
                    aria-label="Close panel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <Tabs
                  value={mode}
                  onValueChange={(v) => setMode(v as Mode)}
                  className="w-full min-w-0 flex-1 flex flex-col min-h-0"
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload">Upload new</TabsTrigger>
                    <TabsTrigger value="pick">Pick from DMS</TabsTrigger>
                  </TabsList>

                  <TabsContent value="upload" className="space-y-3 pt-3">
                    <Input
                      id="file"
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                    />
                    {file && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleUpload}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading…' : 'Upload to DMS'}
                      </Button>
                    )}
                  </TabsContent>

                  <TabsContent value="pick" className="flex-1 min-h-0 flex flex-col gap-2 pt-3">
                    <Input
                      id="dms-search"
                      placeholder="Search DMS by name…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="border rounded flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-background">
                      {docsQuery.isLoading ? (
                        <div className="p-3 text-sm text-muted-foreground">Loading…</div>
                      ) : docs.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground">No documents found</div>
                      ) : (
                        <ul className="divide-y">
                          {docs.map((d) => (
                            <li key={d.id}>
                              <button
                                type="button"
                                onClick={() => handlePick(d.id, d.name)}
                                className="w-full min-w-0 text-left px-3 py-2 text-sm hover:bg-muted"
                              >
                                <div className="font-medium truncate" title={d.name}>{d.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {d.documentDate ?? d.createdAt.slice(0, 10)}
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <>
                {/* Header with statement/line proof toggle + name + change button */}
                <div className="border-b bg-background">
                  {/* Source toggle pills */}
                  <div className="flex items-center gap-1 px-4 pt-2 text-[11px]">
                    <button
                      type="button"
                      onClick={() => { setPreviewDocId(null); setPreviewSource('statement'); }}
                      className={`px-2 py-0.5 rounded border ${previewSource === 'statement' ? 'bg-blue-600 text-white border-blue-700' : 'bg-muted/40 text-muted-foreground border-transparent hover:bg-muted/60'}`}
                    >
                      Statement
                    </button>
                    {previewSource === 'line' && (
                      <button
                        type="button"
                        className="px-2 py-0.5 rounded border bg-sky-600 text-white border-sky-700"
                        disabled
                      >
                        Line proof
                      </button>
                    )}
                  </div>
                  <div className="px-4 py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm truncate" title={docDetail?.name ?? dmsDocumentName ?? ''}>
                        {docDetail?.name ?? dmsDocumentName ?? '—'}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {docDetail?.documentDate ?? docDetail?.createdAt?.slice(0, 10) ?? ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {previewSource === 'statement' ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDmsDocumentId(null);
                            setDmsDocumentName(null);
                            setFile(null);
                          }}
                        >
                          Change
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setPreviewDocId(null); setPreviewSource('statement'); }}
                        >
                          ← Statement
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => setPanelOpen(false)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Close panel (Esc)"
                        aria-label="Close panel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* DMS-style tabs: Preview / Markdown / Data / Tags / Metadata / JSON */}
                <Tabs defaultValue="preview" className="flex-1 min-h-0 flex flex-col">
                  <TabsList className="mx-4 mt-3 grid grid-cols-6">
                    <TabsTrigger value="preview" className="text-xs gap-1">
                      <FileText className="h-3.5 w-3.5" /> Preview
                    </TabsTrigger>
                    <TabsTrigger value="markdown" className="text-xs gap-1">
                      <TextIcon className="h-3.5 w-3.5" /> Markdown
                    </TabsTrigger>
                    <TabsTrigger value="data" className="text-xs gap-1">
                      <Database className="h-3.5 w-3.5" /> Data
                    </TabsTrigger>
                    <TabsTrigger value="tags" className="text-xs gap-1">
                      <TagIcon className="h-3.5 w-3.5" /> Tags
                    </TabsTrigger>
                    <TabsTrigger value="info" className="text-xs gap-1">
                      <Info className="h-3.5 w-3.5" /> Metadata
                    </TabsTrigger>
                    <TabsTrigger value="json" className="text-xs gap-1">
                      <Braces className="h-3.5 w-3.5" /> JSON
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="preview" className="flex-1 min-h-0 mt-3 bg-neutral-100">
                    {previewQuery.isLoading ? (
                      <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                        Loading preview…
                      </div>
                    ) : previewUrl ? (
                      <iframe
                        src={previewUrl}
                        title="Document preview"
                        className="w-full h-full border-0"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                        Preview unavailable
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="markdown" className="flex-1 min-h-0 mt-3 overflow-y-auto bg-background">
                    {docDetailQuery.isLoading ? (
                      <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                    ) : docDetail?.currentVersion?.content ? (
                      <div className="prose prose-sm max-w-none p-4 dark:prose-invert">
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {docDetail.currentVersion.content}
                        </Markdown>
                      </div>
                    ) : (
                      <div className="p-6 text-sm text-muted-foreground">
                        No markdown content yet.
                        {docDetail?.currentVersion?.conversionStatus && (
                          <> Conversion status: <code>{docDetail.currentVersion.conversionStatus}</code></>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="data" className="flex-1 min-h-0 mt-3 overflow-y-auto bg-background p-4">
                    {docMetadata && Object.keys(docMetadata).length > 0 ? (
                      <dl className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-2 text-xs">
                        {Object.entries(docMetadata).map(([k, v]) => (
                          <div key={k} className="contents">
                            <dt className="text-muted-foreground truncate" title={k}>{k}</dt>
                            <dd className="break-words">
                              {v == null
                                ? '—'
                                : typeof v === 'object'
                                  ? <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto">{JSON.stringify(v, null, 2)}</pre>
                                  : String(v)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No extracted data for this document.
                      </p>
                    )}
                  </TabsContent>

                  <TabsContent value="tags" className="flex-1 min-h-0 mt-3 overflow-y-auto bg-background p-4">
                    {docTagIds.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No tags.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {docTagIds.map((tagId) => {
                          const tag = tagsQuery.data?.get(tagId);
                          return (
                            <span
                              key={tagId}
                              className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs"
                              style={tag?.color ? { backgroundColor: tag.color, color: 'white', borderColor: tag.color } : undefined}
                              title={tag?.label ?? tagId}
                            >
                              {tag?.label ?? tagId.slice(0, 8)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="info" className="flex-1 min-h-0 mt-3 overflow-y-auto bg-background">
                    <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-2 text-xs p-4">
                      <dt className="text-muted-foreground">Document ID</dt>
                      <dd className="font-mono truncate" title={dmsDocumentId}>{dmsDocumentId}</dd>
                      <dt className="text-muted-foreground">Original name</dt>
                      <dd className="truncate">{docDetail?.currentVersion?.originalName ?? '—'}</dd>
                      <dt className="text-muted-foreground">Mime type</dt>
                      <dd className="truncate">{docDetail?.currentVersion?.sourceMimeType ?? '—'}</dd>
                      <dt className="text-muted-foreground">Pages</dt>
                      <dd>{docDetail?.currentVersion?.pageCount ?? '—'}</dd>
                      <dt className="text-muted-foreground">Word count</dt>
                      <dd>{docDetail?.currentVersion?.wordCount ?? '—'}</dd>
                      <dt className="text-muted-foreground">Language</dt>
                      <dd>{docDetail?.currentVersion?.language ?? '—'}</dd>
                      <dt className="text-muted-foreground">Size</dt>
                      <dd>
                        {docDetail?.currentVersion?.sourceSize != null
                          ? `${Math.round(docDetail.currentVersion.sourceSize / 1024)} KB`
                          : '—'}
                      </dd>
                      <dt className="text-muted-foreground">Conversion</dt>
                      <dd>{docDetail?.currentVersion?.conversionStatus ?? '—'}</dd>
                      <dt className="text-muted-foreground">Upload source</dt>
                      <dd>{docDetail?.currentVersion?.uploadSource ?? '—'}</dd>
                      <dt className="text-muted-foreground">Created</dt>
                      <dd>{docDetail?.createdAt?.slice(0, 16).replace('T', ' ') ?? '—'}</dd>
                      {driveUrl && (
                        <>
                          <dt className="text-muted-foreground">Google Drive</dt>
                          <dd>
                            <a
                              href={driveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline break-all"
                            >
                              {driveUrl}
                            </a>
                          </dd>
                        </>
                      )}
                    </dl>
                  </TabsContent>

                  <TabsContent value="json" className="flex-1 min-h-0 mt-3 overflow-y-auto bg-background p-4">
                    <pre className="text-[11px] leading-relaxed font-mono bg-muted/40 rounded p-3 overflow-x-auto">
                      {docDetail ? JSON.stringify(docDetail, null, 2) : '—'}
                    </pre>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Note: the Docling JSON is only produced transiently during a reconvert — it is not stored on the document. Use DMS to reconvert if you need to inspect it.
                    </p>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
          {/* ================= END RIGHT COLUMN ================= */}
          </>
          )}
        </form>
      </div>

      <AlertDialog
        open={confirmDeleteIdx !== null}
        onOpenChange={(o) => { if (!o) setConfirmDeleteIdx(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this line?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the line from the draft. If the statement has
              already been reconciled, you'll need to re-run the QB audit to
              update match counts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteIdx !== null) {
                  setLines((prev) => prev.filter((_, i) => i !== confirmDeleteIdx));
                }
                setConfirmDeleteIdx(null);
              }}
            >
              Remove line
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
